/**
 * Session auth uses httpOnly cookies set by the API (no tokens in localStorage).
 * This module keeps device id, optional expiry hint, and fetch helpers.
 */

import { getApiUrl } from '../config/api'
import { invalidateUsersMeGetCache } from './probeUsersMe'
import { clearSessionHint, setSessionHint } from './session'

const TOKEN_EXPIRY_KEY = 'tokenExpiry'
const DEVICE_ID_KEY = 'deviceId'
const LEGACY_AUTH_TOKEN_KEY = 'authToken'

/**
 * Get or generate device ID (still sent on login for server-side session rows).
 */
export const getDeviceId = () => {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY)

  if (!deviceId) {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    ctx.textBaseline = 'top'
    ctx.font = '14px Arial'
    ctx.fillText('Device fingerprint', 2, 2)

    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      new Date().getTimezoneOffset(),
      canvas.toDataURL(),
    ].join('|')

    let hash = 0
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i)
      hash = (hash << 5) - hash + char
      hash = hash & hash
    }

    deviceId = `device_${Math.abs(hash)}_${Date.now()}`
    localStorage.setItem(DEVICE_ID_KEY, deviceId)
  }

  return deviceId
}

/** @deprecated Tokens live in httpOnly cookies only */
export const storeTokens = (_accessToken, _refreshToken, expiresIn = 3600) => {
  invalidateUsersMeGetCache()
  clearStoredExpiryHint()
  clearLegacyAuthToken()
  setSessionHint()
  if (expiresIn) {
    const expiryTime = Date.now() + expiresIn * 1000
    localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString())
  }
}

/** Store only client-side expiry hint from login response */
export const storeSessionExpiry = (expiresInSeconds) => {
  if (!expiresInSeconds) return
  const expiryTime = Date.now() + expiresInSeconds * 1000
  localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString())
}

function clearStoredExpiryHint() {
  localStorage.removeItem(TOKEN_EXPIRY_KEY)
}

function clearLegacyAuthToken() {
  localStorage.removeItem(LEGACY_AUTH_TOKEN_KEY)
}

let forceLogoutInFlight = false

/**
 * True when the client-side expiry timestamp (from login/refresh) has passed.
 * Server cookie should match; this logs out immediately without waiting for the next API call.
 */
export const isClientSessionPastHardExpiry = () => {
  const expiryTime = localStorage.getItem(TOKEN_EXPIRY_KEY)
  if (!expiryTime) return false
  const expiry = parseInt(expiryTime, 10)
  if (Number.isNaN(expiry)) return false
  return Date.now() >= expiry
}

/**
 * Clear all client session state and send the user to login (unless already on a public auth page).
 * Call when the httpOnly session is invalid or past client expiry.
 */
export const forceLogoutExpiredSession = () => {
  if (typeof window === 'undefined') return
  if (forceLogoutInFlight) return
  forceLogoutInFlight = true

  clearTokens()
  try {
    localStorage.removeItem('user')
  } catch (_) {
    /* ignore */
  }

  const path = window.location.pathname || ''
  const publicPrefixes = [
    '/login',
    '/signup',
    '/forgot-password',
    '/reset-password',
    '/accept-invite',
    '/verify-email'
  ]
  const onPublicPage = publicPrefixes.some(
    (p) => path === p || path.startsWith(`${p}/`)
  )

  if (!onPublicPage) {
    window.location.replace('/login?session=expired')
  }

  window.setTimeout(() => {
    forceLogoutInFlight = false
  }, 2000)
}

/**
 * Clear local session hints (cookie cleared by POST /api/auth/logout).
 */
export const clearTokens = () => {
  invalidateUsersMeGetCache()
  clearStoredExpiryHint()
  clearLegacyAuthToken()
  clearSessionHint()
}

/** @deprecated */
export const getAccessToken = () => null

/** @deprecated */
export const getRefreshToken = () => null

export const isTokenExpired = () => {
  const expiryTime = localStorage.getItem(TOKEN_EXPIRY_KEY)
  if (!expiryTime) return false
  const expiry = parseInt(expiryTime, 10)
  const now = Date.now()
  const buffer = 5 * 60 * 1000
  return now >= expiry - buffer
}

/**
 * Refresh session cookie max-age via API (optional body; cookie is enough).
 */
export const refreshAccessToken = async () => {
  const response = await fetch(getApiUrl('/api/auth/refresh'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({}) })

  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    if (response.status === 401) {
      forceLogoutExpiredSession()
    }
    throw new Error(err.detail || err.message || 'Failed to refresh session')
  }

  const data = await response.json()
  if (data.expires_in) {
    storeSessionExpiry(data.expires_in)
  }
  setSessionHint()
  return true
}

/**
 * @deprecated No access token in JS — use refresh if expiry hint is stale.
 */
export const getValidAccessToken = async () => {
  if (isTokenExpired()) {
    await refreshAccessToken()
  }
  return 'cookie'
}

/**
 * Authenticated fetch — relies on httpOnly cookie (global fetch also adds credentials).
 */
export const authenticatedFetch = async (url, options = {}) => {
  let response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}) } })

  if (response.status === 401) {
    try {
      await refreshAccessToken()
      response = await fetch(url, {
        ...options,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(options.headers || {}) } })
    } catch (refreshError) {
      // 401 from /api/auth/refresh already ran forceLogoutExpiredSession inside refreshAccessToken
      throw refreshError
    }
  }

  return response
}
